import AddUserDialog from '@/components/admin/AddUserDialog';
import UserTable from '@/components/admin/UserTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';

import { Input } from '@/components/shadcn-ui/input';
import { getUsers } from '@/network/auth.service';
import { Loader2, Search, Shield, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

const AdminPage = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fetchUsers = useCallback(async debouncedSearch => {
    setLoading(true);

    const filter = {
      'client_details.client_id': import.meta.env.VITE_CLIENT_ID,
    };
    const { data } = await getUsers({ filter: JSON.stringify(filter), search: debouncedSearch });

    setUsers(data.data.result);
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // debounce time (ms)

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchUsers(debouncedSearch);
  }, [debouncedSearch, fetchUsers]);

  return (
    <div className="flex min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <aside className="sticky top-20 hidden h-dvh w-64 flex-col border-r bg-white/80 backdrop-blur md:flex">
        <div className="flex items-center gap-3 border-b p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Admin Panel</p>
            <p className="text-base font-semibold text-slate-900">Control Center</p>
          </div>
        </div>

        <nav className="flex space-y-2 p-4">
          <button className="flex w-full items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white">
            <Users className="h-4 w-4" />
            Users
          </button>
          {/* more nav items later */}
        </nav>

        <div className="mt-auto border-t p-4 text-xs text-slate-400">© {new Date().getFullYear()} Your Company</div>
      </aside>

      {/* Main Content */}
      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-5xl space-y-6">
          {/* Top Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                <Users className="h-6 w-6 text-blue-600" />
                Users
              </h1>
              <p className="text-sm text-slate-500">View and manage all users in your system.</p>
            </div>

            {/* Add User Dialog Trigger */}
            <AddUserDialog />
          </div>

          {/* User List Card */}
          <Card className="relative border-none shadow-sm">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                <Loader2 className="mx-auto animate-spin" />
              </div>
            )}

            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base sm:text-lg">User List</CardTitle>

              <div className="relative w-full sm:w-64">
                <span className="absolute inset-y-0 left-3 flex items-center">
                  <Search className="h-4 w-4 text-slate-400" />
                </span>
                <Input
                  key="search-input"
                  className="pl-9 text-sm"
                  placeholder="Search by name, role, phone..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </CardHeader>

            <CardContent className="overflow-hidden p-0">
              <div className="max-w-full overflow-x-auto">
                <UserTable users={users} />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
